// ─────────────────────────────────────────────────────────────────────────────
// STEP 6 — READ THE FIGURES OFF THE DOCUMENT INSTEAD OF TICKING A BOX.
//
// 🚨 WHAT THIS REPLACES. Two buttons set a boolean. A person opened a title
// commitment and an insurance declaration page, read the numbers, and clicked
// "Confirm Received" — and the numbers, and for the commitment the document
// itself, were discarded. Two files carry title_commitment_received_at with
// title_commitment_document_id NULL.
//
// 🔑 EVERYTHING HERE IS SERVED, NOT TYPED. The document list, the fields each
// one carries and their labels all come from step6_capture on the detail
// payload, which derives them from constants/captureDocuments.js and validates
// them against the closing-cost ledger at boot. A field list written here would
// be the copy that drifts.
//
// ⚠️ THE THREE STATES RENDER DIFFERENTLY ON PURPOSE. "Reviewed before capture
// existed" is not "not done" — see constants/figureCaptureEra.js — and showing
// it as a bare red X would read as a regression on files where nothing
// regressed.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';

const STATE_STYLE = {
  captured:            { dot: '#16a34a', text: '#166534', label: 'Captured' },
  claimed_pre_capture: { dot: '#f59e0b', text: '#92400e', label: 'Reviewed before capture existed' },
  pending:             { dot: '#d1d5db', text: '#6b7280', label: 'Awaiting document and figures' },
};

const money = (n) => `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 })}`;

export default function Step6FigureCapture({ docKey, item, action, documentId, onCaptured }) {
  const [figures, setFigures] = useState({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  if (!item) return null;

  const style = STATE_STYLE[item.state] || STATE_STYLE.pending;
  const set = (field, part, v) =>
    setFigures((f) => ({ ...f, [field]: { ...(f[field] || {}), [part]: v } }));

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await action({ document_id: documentId || item.documentId, figures });
      onCaptured?.();
    } catch (e) {
      // The server's refusals say what to supply — surface them verbatim rather
      // than replacing them with a generic failure.
      setErr(e?.message || 'Capture failed.');
    } finally { setBusy(false); }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginBottom: 6 }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: style.dot, flexShrink: 0 }} />
        <span style={{ color: style.text }}>{item.label} — {style.label}</span>
      </div>

      {item.state === 'claimed_pre_capture' && (
        <div style={{ fontSize: 11, color: '#92400e', background: '#fffbeb',
                      border: '1px solid #fde68a', borderRadius: 6, padding: '6px 8px', marginBottom: 8 }}>
          Marked reviewed on {item.claimedAt ? new Date(item.claimedAt).toLocaleDateString() : 'an earlier date'},
          before figures were captured. The artifact was not retained, so the figures on it are unknown —
          re-capture from the document to close it. Nothing was backfilled to make this read as done.
        </div>
      )}

      {item.state === 'captured' && (
        <div style={{ fontSize: 12, color: '#374151' }}>
          {item.fields.map((f) => {
            const v = item.values?.[f];
            return (
              <div key={f} style={{ marginBottom: 2 }}>
                <strong>{item.fieldLabels?.[f] || f}:</strong>{' '}
                {v ? (f.endsWith('Months') ? `${v.value} months` : money(v.value)) : '—'}
                {v?.basis ? <span style={{ color: '#6b7280' }}> · {v.basis}</span> : null}
              </div>
            );
          })}
        </div>
      )}

      {item.state !== 'captured' && (
        <div>
          {!item.hasDocument && !documentId && (
            <div style={{ fontSize: 11, color: '#991b1b', marginBottom: 6 }}>
              Upload the {item.label} first — a receipt with no artifact is what this replaces.
            </div>
          )}
          {item.fields.map((f) => (
            <div key={f} style={{ display: 'flex', gap: 6, marginBottom: 4 }}>
              <input
                type="number" step="0.01" min="0"
                placeholder={item.fieldLabels?.[f] || f}
                value={figures[f]?.value ?? ''}
                onChange={(e) => set(f, 'value', e.target.value)}
                style={{ width: 150, fontSize: 12, padding: '4px 6px',
                         border: '1px solid #d1d5db', borderRadius: 4 }} />
              <input
                type="text"
                placeholder="Read off which line / schedule"
                value={figures[f]?.basis ?? ''}
                onChange={(e) => set(f, 'basis', e.target.value)}
                style={{ flex: 1, fontSize: 12, padding: '4px 6px',
                         border: '1px solid #d1d5db', borderRadius: 4 }} />
            </div>
          ))}
          {err && (
            <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4, whiteSpace: 'pre-wrap' }}>{err}</div>
          )}
          <button
            onClick={submit}
            disabled={busy || (!item.hasDocument && !documentId)}
            style={{ marginTop: 6, fontSize: 12, padding: '5px 10px', borderRadius: 6,
                     border: '1px solid #2563eb', background: busy ? '#93c5fd' : '#2563eb',
                     color: '#fff', cursor: busy ? 'default' : 'pointer' }}>
            {busy ? 'Recording…' : `Record ${item.label} figures`}
          </button>
        </div>
      )}
    </div>
  );
}
